import React from 'react'
import { Link as ClientLink } from 'react-router-dom'
import { HashLink } from 'react-router-hash-link'

import { useEditor } from '@toasttab/sites-components'
import classNames from 'classnames'
import urljoin from 'url-join'
import isURL from 'validator/es/lib/isURL'

import { t } from 'src/translations'

import {
  AttributionContextType,
  useAttribution
} from 'shared/components/common/attribution_context/AttributionContext'

import { usePageDomains } from 'public/js/PageDomainContext'
import { useOptionalRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useCrossAppNavigation } from 'shared/js/hooks/useCrossAppNavigation'
import { getSessionManager } from 'src/lib/js/guest-analytics/sessionManager'
import { useCookieConsentContext } from 'src/public/js/cookie_consent/CookieConsentContext'

import { server } from 'config'

const NEW_TAB_PROPS = {
  target: '_blank',
  rel: 'noopener noreferrer'
}

type AttributionOverride = {
  attributionOverride?: AttributionContextType // override AttributionContext link attribution fields.
  blockAttribution?: boolean // if true, removes any and all attribution on a link.
  sameTab?: boolean
}

export type LinkProps = {
  ariaLabel?: string
  isBold?: boolean
  icon?: JSX.Element
  state?: any
  testId?: string
} & React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
> &
  AttributionOverride

// Returns True if urlStr links to toasttab.com or toast.app. False otherwise.
export const isToastTabLink = (urlStr: string): boolean => {
  try {
    const url = new URL(urlStr)
    return (
      url.hostname.endsWith('toasttab.com') ||
      url.hostname.endsWith('toast.app')
    )
  } catch (error) {
    // urlStr is not URL
    return false
  }
}

// Returns true if URL is a reservation or gift card link that should receive redirect params.
export const isRedirectParamLink = (url: URL): boolean => {
  const RESERVATION_HOSTS = new Set([
    // legacy reservations host
    'tables.toasttab.com',
    'tables.preprod.eng.toasttab.com', // preprod

    // toast.app is the new reservations host
    'toast.app',
    'preprod.eng.toast.app' // preprod
  ])
  const GIFT_CARD_HOSTS = new Set([
    'order.toasttab.com',
    'order-pre.toasttab.com' // preprod
  ])
  return (
    RESERVATION_HOSTS.has(url.hostname) ||
    (GIFT_CARD_HOSTS.has(url.hostname) &&
      (url.pathname.includes('/egiftcard') ||
        url.pathname.includes('/giftcards')))
  )
}

// to make the links more accessible for keyboard access
export const handleLinkKeyDown = (e: React.KeyboardEvent, href?: string) => {
  if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
    e.preventDefault() // Prevent the default space key action (page scroll)
    e.stopPropagation()
    if (href?.startsWith('#')) {
      const el = document.getElementById(href.slice(1))
      if (el) {
        el.scrollTo()
        el.focus()
      }
    } else {
      window.location.href = '' + href // Navigate to the link's href
    }
  }
}

const InactiveLink = (props: LinkProps) => {
  return (
    <a
      className={classNames(props.className, props.isBold ? 'bold' : '')}
      title={t('link.links-are-inactive-in-editor-mode')}
      style={props.style}
      aria-label={props.ariaLabel}
    >
      {props.children}
    </a>
  )
}

const Link = (props: LinkProps) => {
  const {
    isBold,
    ariaLabel,
    attributionOverride,
    blockAttribution,
    sameTab,
    ...linkProps
  } = props
  const { isEditor } = useEditor()
  const { host, pageDomainOverrides, overriddenDomains, rootDomain } =
    usePageDomains()
  const restaurantContext = useOptionalRestaurant()
  const { analyticsConsent } = useCookieConsentContext()
  const crossesApps = useCrossAppNavigation()

  let linkAttribution = useAttribution()
  if (blockAttribution) {
    linkAttribution = {}
  } else if (attributionOverride) {
    linkAttribution = { ...linkAttribution, ...attributionOverride }
    // Remove keys with empty, undefined, or falsy values.
    linkAttribution = Object.fromEntries(
      Object.entries(linkAttribution).filter(([_, v]) => !!v)
    )
  }

  if (!props.href) {
    return null
  }
  if (isEditor) {
    // links inside the Editor should do nothing.
    return <InactiveLink {...props}>{props.children}</InactiveLink>
  }
  const maybeWithIcon = (_children: any) => {
    if (props.icon) {
      return (
        <div className='linkChildrenWrapper'>
          {props.icon}
          {_children}
        </div>
      )
    } else {
      return _children
    }
  }

  // A destination the other app serves can't be reached by a client-side
  // transition — React Router would try to render this app's routes for it.
  // A plain anchor makes the browser do a real navigation, which lets the dual
  // router hand the request to whichever app owns the path.
  //
  // The href stays relative on purpose: it renders identically on server and
  // client, so there's no hydration mismatch and no dependence on
  // window.location.origin.
  const crossAppLink = (href: string) => {
    // icon, state and testId aren't <a> attributes: icon is a JSX element, state
    // belongs to react-router, and testId is emitted as data-testid below.
    // Spreading them would produce React unknown-prop warnings and stringified
    // attributes like icon="[object Object]".
    const { icon, state, testId, ...anchorProps } = linkProps

    return (
      <a
        {...anchorProps}
        href={href}
        aria-label={ariaLabel}
        className={classNames(props.className, { bold: isBold })}
        onKeyDown={(e) => handleLinkKeyDown(e, href)}
        style={props.style}
        data-testid={testId}
      >
        {maybeWithIcon(props.children)}
      </a>
    )
  }

  if (
    isURL(props.href) ||
    props.href.startsWith('tel:') ||
    props.href.startsWith('mailto:')
  ) {
    if (isToastTabLink(props.href)) {
      const url = new URL(props.href)

      // Append redirect params for reservation and gift card links
      if (
        isRedirectParamLink(url) &&
        restaurantContext?.restaurant?.id &&
        !blockAttribution
      ) {
        url.searchParams.append('toast_sg', restaurantContext.restaurant.id)
        if (analyticsConsent) {
          url.searchParams.append(
            'toast_ss',
            getSessionManager().getSessionGuid()
          )
        }
        url.searchParams.append('toast_src', 'website_link') // must be website_link!
      }

      Object.entries(linkAttribution).forEach(([key, value]) => {
        url.searchParams.append(key, value)
      })

      return (
        <a
          {...linkProps}
          href={url.toString()}
          aria-label={`${ariaLabel ?? ''} ${
            ariaLabel && !sameTab ? t('opens-in-new-tab') : ''
          }`}
          className={classNames(props.className, { bold: isBold })}
          {...(sameTab ? {} : NEW_TAB_PROPS)}
          onKeyDown={(e) => handleLinkKeyDown(e, url.toString())}
          style={props.style}
        >
          {maybeWithIcon(props.children)}
        </a>
      )
    }
    return (
      <a
        {...linkProps}
        aria-label={ariaLabel}
        {...(sameTab ? {} : NEW_TAB_PROPS)}
      >
        {maybeWithIcon(props.children)}
      </a>
    )
  } else if (props.href.indexOf('#') > -1) {
    // HashLink scrolls to the anchor after a client-side transition, which can't
    // work when the anchor lives on a page the other app renders.
    if (crossesApps(props.href)) {
      return crossAppLink(props.href)
    }

    // Parse the href to separate pathname and hash
    const [pathname, hash] = props.href.includes('#')
      ? props.href.split('#', 2)
      : [props.href, '']

    return (
      <HashLink
        aria-label={ariaLabel}
        style={props.style}
        to={{
          pathname: pathname || '/',
          hash: hash ? `#${hash}` : undefined,
          state: props.state
        }}
        onKeyDown={(e) => handleLinkKeyDown(e, props?.href)}
        className={classNames(props.className, isBold ? 'bold' : '')}
        onClick={props.onClick}
      >
        {maybeWithIcon(props.children)}
      </HashLink>
    )
  } else {
    if (isToastTabLink(props.href)) {
      const utmParamString = new URLSearchParams(linkAttribution).toString()
      return (
        <ClientLink
          to={{
            pathname: props.href,
            search: utmParamString,
            state: props.state
          }}
          aria-label={ariaLabel}
          className={props.className}
          style={props.style}
          onClick={props.onClick}
        >
          {maybeWithIcon(props.children)}
        </ClientLink>
      )
    }

    // Check if one of the customer's other domains/subdomains is the preferred host for the link's path
    const linkDomain = preferredDomainForPath(props.href, pageDomainOverrides)

    if (linkDomain) {
      if (host !== linkDomain) {
        // This link is mapped to a domain that isn't the current domain, so link to that domain.
        // props.onClick is not passed to the anchor tag here because it can cause changes to the current page before the new page renders,
        // which is jarring
        const url = urljoin(`${server.protocol}://${linkDomain}`, props.href)
        return (
          <a
            href={url}
            aria-label={ariaLabel}
            className={classNames(props.className, { bold: isBold })}
            onKeyDown={(e) => handleLinkKeyDown(e, url)}
            style={props.style}
          >
            {maybeWithIcon(props.children)}
          </a>
        )
      } else {
        if (crossesApps(props.href)) {
          return crossAppLink(props.href)
        }

        // Already on the mapped domain, so stay on it.
        return (
          <ClientLink
            to={{ pathname: props.href, state: props.state }}
            aria-label={ariaLabel}
            className={classNames(props.className, { bold: isBold })}
            onKeyDown={(e) => handleLinkKeyDown(e, props?.href)}
            style={props.style}
            onClick={props.onClick}
          >
            {maybeWithIcon(props.children)}
          </ClientLink>
        )
      }
    } else if (overriddenDomains.has(host)) {
      // The current domain is mapped to a page and this link is not mapped to a domain,
      // so go to the root domain.
      const url = urljoin(`${server.protocol}://${rootDomain}`, props.href)
      return (
        <a
          href={url}
          aria-label={ariaLabel}
          className={classNames(props.className, { bold: isBold })}
          onKeyDown={(e) => handleLinkKeyDown(e, url)}
          style={props.style}
        >
          {maybeWithIcon(props.children)}
        </a>
      )
    }

    if (crossesApps(props.href)) {
      return crossAppLink(props.href)
    }

    // the current domain is not mapped to a page, and the link is not mapped to a domain,
    // so stay on this domain
    return (
      <ClientLink
        to={{ pathname: props.href, state: props.state }}
        aria-label={ariaLabel}
        className={classNames(props.className, { bold: isBold })}
        onKeyDown={(e) => handleLinkKeyDown(e, props?.href)}
        style={props.style}
        onClick={props.onClick}
        data-testid={props.testId}
      >
        {maybeWithIcon(props.children)}
      </ClientLink>
    )
  }
}

/**
 * Get the overriding/preferred domain for the given path.
 * @param path A path, relative to the domain root.
 * @param pageDomainOverrides A map of path prefixes to their overriding domains.
 * @returns The preferred/overriding domain for the given path. Returns undefined if there are no preferred/overriding domains for the given path.
 */
export const preferredDomainForPath = (
  path: string,
  pageDomainOverrides: Map<string, string>
): string | undefined => {
  if (pageDomainOverrides.size === 0) {
    return undefined
  }
  // Check if the first part of the path is overridden by a domain.
  // For example, /order/abc should use the domain that is mapped to /order
  const pathParts = path.split('/')
  return pathParts.length > 1 && pathParts[0] === ''
    ? pageDomainOverrides.get('/' + pathParts[1])
    : pageDomainOverrides.get(path)
}

export default Link
